import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Language as BrowserIcon,
  EventNote as SchedulesIcon,
  RecordVoiceOver as TalkingPalIcon,
  SportsEsports as GamesIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../contexts/DeviceContext';
import { appVisibilityService } from '../services/appVisibilityService';
import {
  AppKey,
  AppVisibilitySettings,
  DEFAULT_APP_VISIBILITY_SETTINGS,
} from '../types/appVisibility';

interface AppRow {
  key: AppKey;
  Icon: React.ElementType;
}

// Order matches the launcher entries of the CTube tablet app
const APP_ROWS: AppRow[] = [
  { key: 'browser', Icon: BrowserIcon },
  { key: 'schedules', Icon: SchedulesIcon },
  { key: 'talkingpal', Icon: TalkingPalIcon },
  { key: 'games', Icon: GamesIcon },
];

/**
 * Settings card: which child-facing apps of the tablet suite are visible on the device.
 * Hidden apps disappear from the tablet's home screen and are closed immediately.
 * Backed by devices/{id}/config/appVisibility (contracts/firebase-schema.md §2.4).
 */
const AppVisibilitySection: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, showSnackbar } = useDevice();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AppVisibilitySettings>(
    DEFAULT_APP_VISIBILITY_SETTINGS
  );

  const isViewer = selectedDevice?.role === 'viewer';

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!selectedDevice?.id) return;

    try {
      setLoading(true);
      const data = await appVisibilityService.getSettings(selectedDevice.id);
      setSettings(data);
    } catch (error) {
      console.error('Failed to load app visibility settings:', error);
      showSnackbar(t('appVisibility.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Optimistic toggle with a partial PUT; revert to the previous value on error
  const handleToggle = useCallback(
    async (key: AppKey, visible: boolean) => {
      if (!selectedDevice?.id || isViewer) return;

      const previous = settings;
      setSettings((prev) => ({ ...prev, [key]: visible }));

      try {
        setSaving(true);
        const updated = await appVisibilityService.updateSettings(selectedDevice.id, {
          [key]: visible,
        });
        setSettings(updated);
        showSnackbar(t('appVisibility.settingsSaved'), 'success');
      } catch (error) {
        console.error('Failed to save app visibility settings:', error);
        setSettings(previous);
        showSnackbar(t('appVisibility.failedToSave'), 'error');
      } finally {
        setSaving(false);
      }
    },
    [selectedDevice?.id, isViewer, settings, showSnackbar, t]
  );

  if (!selectedDevice) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('appVisibility.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('appVisibility.description')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {APP_ROWS.map(({ key, Icon }, index) => (
              <React.Fragment key={key}>
                {index > 0 && <Divider />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.5,
                  }}
                >
                  <Icon color={settings[key] ? 'primary' : 'disabled'} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1">
                      {t(`appVisibility.apps.${key}.name`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`appVisibility.apps.${key}.description`)}
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings[key]}
                    onChange={(e) => handleToggle(key, e.target.checked)}
                    disabled={isViewer || saving}
                    inputProps={{ 'aria-label': t(`appVisibility.apps.${key}.name`) }}
                  />
                </Box>
              </React.Fragment>
            ))}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              {t('appVisibility.hiddenHint')}
            </Typography>

            {/* Saving indicator */}
            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t('common.saving')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AppVisibilitySection;
